import { INGREDIENTS_TREE_RDB } from '__constants__'
import { toCamelCase } from '../transform'

/**
 * Adds a node optimistically to the ingredients tree structure.
 * This function creates a temporary node and adds it to the tree before the backend confirms.
 *
 * @param {Object} tree - The current ingredients tree object.
 * @param {Object} nodeData - The node data to add.
 * @param {string} nodeData.name - The name of the node.
 * @param {string} nodeData.parentPath - The path of the parent node (null for root).
 * @param {Object} nodeData.translations - The translations of the node.
 * @param {string} nodeData.eNumber - The E number of the node.
 * @param {string[]} nodeData.synonyms - The synonyms of the node.
 * @param {string[]} nodeData.hiddenSynonyms - The hidden synonyms of the node.
 * @param {string} nodeData.description - The description of the node.
 * @param {string[]} nodeData.productsIds - The IDs of the products associated with the node.
 * @param {boolean} nodeData.isIngredient - Whether the node is an ingredient.
 * @param {boolean} nodeData.isDeclared - Whether the node is declared.
 * @param {boolean} nodeData.isVisibleInRecipes - Whether the node is visible in recipes.
 * @param {string} nodeData.userId - The ID of the current user.
 * @param {string} nodeData.visibility - Visibility level.
 * @returns {Object} - The updated tree with the new node added.
 */
const addNodeToTree = (tree, nodeData) => {
  if (!tree || typeof tree !== 'object') {
    return tree
  }

  // Generate node ID from name
  const id = toCamelCase(nodeData.name)
  if (!id) {
    console.error('addNodeToTree: Failed to generate id from name', {
      name: nodeData.name
    })
    return tree
  }

  // Create a deep copy of the tree
  const updatedTree = JSON.parse(JSON.stringify(tree))

  // Create node path
  const path = nodeData.parentPath
    ? `${nodeData.parentPath}/children/${id}`
    : `${INGREDIENTS_TREE_RDB}/${id}`

  // Get parent node to calculate parentIds
  let parent = null
  if (nodeData.parentPath) {
    const pathParts = nodeData.parentPath.split('/').slice(1)
    parent = pathParts.reduce((acc, part) => {
      if (part === 'children') return acc
      return acc?.[part] || null
    }, updatedTree)
  }

  const parentIds = parent
    ? parent?._parentIds?.length
      ? parent?._parentIds?.concat?.(parent._id)
      : [parent._id]
    : null

  // Create node object
  const currentDate = new Date().toISOString()
  const node = {
    _id: id,
    _path: path,
    _parentIds: parentIds,
    _isUpdated: false,
    _createdAt: currentDate,
    _updatedAt: null,
    _createdBy: nodeData.userId,
    _updatedBy: null,
    name: nodeData.name,
    children: null,
    isTopLevel: !parentIds?.length,
    isLastLevel: true,
    isIngredient: nodeData.isIngredient || false,
    isDeclared: nodeData.isDeclared || false,
    isVisibleInRecipes: nodeData.isVisibleInRecipes || false,
    translations: nodeData.translations || null,
    eNumber: nodeData.eNumber || null,
    synonyms: nodeData.synonyms || null,
    hiddenSynonyms: nodeData.hiddenSynonyms || null,
    description: nodeData.description || null,
    productsIds: nodeData.productsIds || null,
    visibility: nodeData.visibility || null
  }

  // Add node to tree
  if (nodeData.parentPath) {
    // Add to parent's children
    if (!parent) {
      console.error('addNodeToTree: Parent not found', {
        parentPath: nodeData.parentPath
      })
      return tree
    }

    if (!parent.children) {
      parent.children = {}
    }
    parent.children[id] = node

    // Update parent's isLastLevel
    parent.isLastLevel = false
  } else {
    // Add as root node
    updatedTree[id] = node
  }

  return updatedTree
}

export default addNodeToTree
